import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

interface FirmDetails {
  name?: string;
  address_1?: string;
  address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  currency?: string;
}

interface StripePaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (paymentIntentId: string) => void;
  stripePublishableKey: string;
  clientSecret: string;
  paymentIntentId: string;
  amount: string;
  firmDetails?: FirmDetails | null;
}

// Initialize Stripe outside component to avoid re-initialization
let stripePromise: Promise<Stripe | null> | null = null;

const getStripe = (publishableKey: string) => {
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

const PaymentForm: React.FC<{
  clientSecret: string;
  paymentIntentId: string;
  amount: string;
  onSuccess: (paymentIntentId: string) => void;
  onError: (message: string) => void;
  onClose: () => void;
  firmDetails?: FirmDetails | null;
}> = ({ clientSecret, paymentIntentId, amount, onSuccess, onError, onClose, firmDetails }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setProcessing(false);
      return;
    }

    try {
      // Build billing details for Indian export transactions
      // Required for ALL currencies when firm country is India (per Indian export regulations)
      const country = firmDetails?.country?.toLowerCase() || '';
      const isIndianFirm = country === 'india';

      let billingDetails: any = undefined;
      
      if (firmDetails) {
        // Convert country name to ISO code
        const getCountryCode = (countryName: string): string => {
          const countryMap: { [key: string]: string } = {
            'india': 'IN',
            'united states': 'US',
            'united states of america': 'US',
            'usa': 'US',
            'united kingdom': 'GB',
            'uk': 'GB',
            'canada': 'CA',
            'australia': 'AU',
          };
          
          const normalized = countryName.toLowerCase().trim();
          if (countryMap[normalized]) {
            return countryMap[normalized];
          }
          
          // If already a 2-letter code, return uppercase
          if (normalized.length === 2) {
            return normalized.toUpperCase();
          }
          
          // Default to IN for India-related names
          if (normalized.includes('india')) {
            return 'IN';
          }
          
          return normalized.toUpperCase();
        };

        if (firmDetails.name && firmDetails.address_1 && firmDetails.city && 
            firmDetails.state && firmDetails.zip_code && firmDetails.country) {
          billingDetails = {
            name: firmDetails.name,
            address: {
              line1: firmDetails.address_1,
              ...(firmDetails.address_2 && { line2: firmDetails.address_2 }),
              city: firmDetails.city,
              state: firmDetails.state,
              postal_code: firmDetails.zip_code,
              country: getCountryCode(firmDetails.country),
            },
          };
        }
      }

      // Confirm payment with Stripe
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          ...(billingDetails && { billing_details: billingDetails }),
        }
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        onError(confirmError.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Pass payment intent ID to success handler
        onSuccess(paymentIntent.id);
      } else {
        setError('Payment was not successful');
        onError('Payment was not successful');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'An error occurred during payment';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Amount: ${parseFloat(amount).toFixed(2)}
        </Typography>
      </Box>
      
      <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <CardElement options={cardElementOptions} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DialogActions>
        <Button onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing}
        >
          {processing ? <CircularProgress size={24} /> : `Pay $${parseFloat(amount).toFixed(2)}`}
        </Button>
      </DialogActions>
    </form>
  );
};

const StripePaymentDialog: React.FC<StripePaymentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  stripePublishableKey,
  clientSecret,
  paymentIntentId,
  amount,
  firmDetails,
}) => {
  const [error, setError] = useState<string | null>(null);
  const stripe = getStripe(stripePublishableKey);

  const handleSuccess = (paymentIntentId: string) => {
    setError(null);
    onSuccess(paymentIntentId);
  };

  const handleError = (message: string) => {
    setError(message);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete Payment</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {stripe && clientSecret && (
          <Elements stripe={stripe} options={{ clientSecret }}>
            <PaymentForm
              clientSecret={clientSecret}
              paymentIntentId={paymentIntentId}
              amount={amount}
              onSuccess={handleSuccess}
              onError={handleError}
              onClose={onClose}
              firmDetails={firmDetails}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StripePaymentDialog;

